import React, { useEffect, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { exitReasonService, saveOfferService } from '../../services';
import SaveOffer from '../SaveOffer';
import { isOfferDrawable } from '../../pages/Goodbye/saveOfferPresentation';
import styles from './index.module.scss';
import {
    QUESTION_HEADING,
    QUESTION_INTRO,
    COMMENT_LABEL,
    FOOTNOTE,
    SEND_LABEL,
    SKIP_LABEL,
    DISMISS_LABEL,
    CLOSING_HEADING,
    CLOSING_BODY,
    CONTACT_LINE,
    HELP_EMAIL,
    HELP_PHONE,
    REASONS_UNAVAILABLE,
    PICK_ONE_FIRST,
    SEND_FAILED,
    EXIT_REASON_COMMENT_MAX,
} from '../../pages/Goodbye/exitReasonPresentation';

// The exit-reason question, shared by both doors Andrew approved on 2026-10-01.
//
// variant="goodbye": the standalone page, reached from a link in our cancellation reply.
// No login, no mail sent, and skipping is a real option.
//
// variant="account": the quiet entry under the subscription details. This one IS the
// cancellation request, so sending it tells the team.
//
// The reasons are fetched rather than listed here on purpose. See the note at the top of
// pages/Goodbye/exitReasonPresentation.js.
const ExitReasonQuestion = ({ variant = 'goodbye', onDismiss }) => {
    const [reasons, setReasons] = useState(null);
    const [loadFailed, setLoadFailed] = useState(false);
    const [picked, setPicked] = useState('');
    const [comment, setComment] = useState('');
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);
    const [done, setDone] = useState(false);
    const [notifyFailed, setNotifyFailed] = useState(null);
    const [offer, setOffer] = useState(null);

    useEffect(() => {
        let dropped = false;

        exitReasonService.listReasons()
            .then((res) => {
                if (dropped) return;
                const list = res?.data?.reasons;
                if (Array.isArray(list) && list.length) setReasons(list);
                else setLoadFailed(true);
            })
            .catch(() => {
                if (!dropped) setLoadFailed(true);
            });

        return () => { dropped = true; };
    }, []);

    // Ask the server for the offer and draw it if there is one. Returns true when the
    // offer screen has taken over, false when the caller should show the closing copy.
    const askForTheSaveOffer = async () => {
        try {
            const res = await saveOfferService.offer();

            if (!isOfferDrawable(res?.data)) return false;

            setOffer(res.data);

            return true;
        } catch (err) {
            // No offer rather than an error. Their cancellation request is already with
            // us, so the worst case here is that a studio we might have kept is not asked
            // to stay, and the right screen is the one that confirms what they came to do.
            return false;
        }
    };

    const send = async () => {
        if (!picked) {
            setError(PICK_ONE_FIRST);
            return;
        }

        setSending(true);
        setError(null);

        const body = { reason: picked };
        const note = comment.trim();
        if (note) body.comment = note;

        try {
            if (variant === 'account') {
                await exitReasonService.submitFromAccount(body);

                // The cancellation request is recorded and we have been told, so the
                // studio is safe from here whatever happens next. Only now do we ask
                // whether it should see the discount.
                //
                // The order matters and is not interchangeable: asking first would mean a
                // studio that abandons the offer screen has told nobody it is leaving. The
                // request always lands first, and the offer is a chance to undo it.
                //
                // Nothing here decides who gets an offer. The server does, from the
                // studio's own subscription and whether it has had one before, and it
                // answers offered: false whenever the answer is no. Any failure, any
                // missing value, and this falls straight through to the ordinary closing
                // copy, because a leaving studio should never be shown a broken discount.
                const shown = await askForTheSaveOffer();

                if (shown) return;
            } else {
                await exitReasonService.submitFromGoodbyePage(body);
            }

            setDone(true);
        } catch (err) {
            const res = err?.response;

            // The server answers 502 with recorded: true when the answer was saved but
            // the cancellation request could not be passed on to us. That case must NOT
            // fall through to the ordinary closing copy, because that copy tells the
            // studio their request is with us, and here nobody has been told. It is the
            // one failure on this screen that can cost a studio money, so it gets the
            // server's own message and our phone number instead.
            if (res?.status === 502 && res?.data?.recorded) {
                setNotifyFailed(res?.data?.message || SEND_FAILED);
                return;
            }

            setError(res?.data?.message || SEND_FAILED);
        } finally {
            setSending(false);
        }
    };

    const contact = (
        <p className={styles.contact}>
            {CONTACT_LINE} <a href={`mailto:${HELP_EMAIL}`}>{HELP_EMAIL}</a> or {HELP_PHONE}.
        </p>
    );

    // The offer comes before the closing copy, because a studio that is about to be asked
    // to stay has not finished leaving yet. Declining clears it and the closing copy below
    // is what they see, which is also why there is no second ask: onDeclined sets done,
    // and done has no path back to here.
    if (offer) {
        return (
            <div className={styles.question}>
                <SaveOffer offer={offer} onDeclined={() => { setOffer(null); setDone(true); }} />
            </div>
        );
    }

    if (notifyFailed) {
        return (
            <div className={styles.question}>
                <h2 className={styles.heading}>{CLOSING_HEADING}</h2>
                <p className={styles.warning} role="alert">{notifyFailed}</p>
                {contact}
            </div>
        );
    }

    if (done) {
        return (
            <div className={styles.question}>
                <h2 className={styles.heading}>{CLOSING_HEADING}</h2>
                <p className={styles.body}>{CLOSING_BODY[variant]}</p>
                {contact}
            </div>
        );
    }

    if (loadFailed) {
        return (
            <div className={styles.question}>
                <p className={styles.warning} role="alert">{REASONS_UNAVAILABLE}</p>
            </div>
        );
    }

    if (!reasons) {
        return (
            <div className={styles.question}>
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </div>
        );
    }

    return (
        <div className={styles.question}>
            <h2 className={styles.heading}>{QUESTION_HEADING}</h2>
            <p className={styles.body}>{QUESTION_INTRO}</p>

            <fieldset className={styles.reasons}>
                {reasons.map(({ code, label, hint }) => (
                    <label className={styles.reason} key={code} htmlFor={`exit-reason-${code}`}>
                        <input
                            type="radio"
                            id={`exit-reason-${code}`}
                            name="exit-reason"
                            value={code}
                            checked={picked === code}
                            onChange={() => { setPicked(code); setError(null); }}
                        />
                        <span className={styles.reasonLabel}>{label}</span>
                        {hint ? <span className={styles.hint}>{hint}</span> : null}
                    </label>
                ))}
            </fieldset>

            <label className={styles.commentLabel} htmlFor="exit-reason-comment">
                {COMMENT_LABEL}
            </label>
            <textarea
                id="exit-reason-comment"
                className={styles.comment}
                maxLength={EXIT_REASON_COMMENT_MAX}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
            />

            {error ? <p className={styles.warning} role="alert">{error}</p> : null}

            <div className={styles.actions}>
                <Button variant="primary" onClick={send} disabled={sending}>
                    {sending ? 'Sending...' : SEND_LABEL[variant]}
                </Button>

                {variant === 'account' ? (
                    <Button variant="link" onClick={onDismiss} disabled={sending}>
                        {DISMISS_LABEL}
                    </Button>
                ) : (
                    <Button variant="link" onClick={() => setDone(true)} disabled={sending}>
                        {SKIP_LABEL}
                    </Button>
                )}
            </div>

            <p className={styles.foot}>{FOOTNOTE}</p>
        </div>
    );
};

export default ExitReasonQuestion;
